'use client';

import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { CellTokenAmountPrice, NetworkAvatar as Network, type SrcAvatarProps, VaultCell } from '@morpho-org/ui-v6/client';
import { CellSimpleText, Skeleton } from '@morpho-org/ui-v6/server';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { abbreviateAddress } from '@repo/utils';
import { useMemo } from 'react';
import { ButtonLink } from '@/components/common/ButtonLink';
import { TimestampCell } from '@/components/common/Cells/TimestampCell';
import { InternalLink } from '@/components/common/InternalLink';
import { getVaultUrl } from '@/helpers/routing';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import type { EarnActivityTableRow } from './DashboardActivity.types';
import { type DashboardActivityTableFeatures, EARN_COLUMN_IDS } from './DashboardActivity.config';
export const COLUMN_LABELS: Partial<Record<keyof typeof EARN_COLUMN_IDS, string>> = {
  [EARN_COLUMN_IDS.Timestamp]: 'Date',
  [EARN_COLUMN_IDS.Type]: 'Type',
  [EARN_COLUMN_IDS.AssetsUsd]: 'Amount',
  [EARN_COLUMN_IDS.Vault]: 'Vault',
  [EARN_COLUMN_IDS.TransactionHash]: 'Transaction',
  [EARN_COLUMN_IDS.Network]: 'Network'
};
const avatarProps: Omit<SrcAvatarProps, 'src'> = {
  size: 'small'
};
export const columnsDefs: ColumnDefWithSkeleton<DashboardActivityTableFeatures, EarnActivityTableRow>[] = [{
  id: EARN_COLUMN_IDS.Network,
  accessorFn: row => row.chainId,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.Network}</span>
        </TooltipTrigger>
        <TooltipContent>The network where the transaction occurred.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    return <div className="flex items-center">
          <Network chainId={row.original.chainId} srcAvatarProps={avatarProps} />
        </div>;
  },
  enableSorting: true,
  skeletonCell: <Skeleton className="h-md w-5" variant="circle" />,
  filterFn: (row, _, filterValue) => filterValue.includes(row.original.chainId)
}, {
  id: EARN_COLUMN_IDS.Timestamp,
  accessorFn: row => row.timestamp,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.Timestamp}</span>
        </TooltipTrigger>
        <TooltipContent>Timestamp of when the transaction occurred.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <TimestampCell timestamp={row.original.timestamp} />,
  enableSorting: true,
  skeletonCell: <Skeleton className="h-md w-[122px] rounded-[20px]" />
}, {
  id: EARN_COLUMN_IDS.Vault,
  accessorFn: row => row.vaultName,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{
        marginLeft: '7px'
      }}>
            {COLUMN_LABELS.Vault}
          </span>
        </TooltipTrigger>
        <TooltipContent>The vault where the transaction occurred.</TooltipContent>
      </Tooltip>,
  cell: function VaultLinkCell({
    row
  }) {
    const {
      isSelf
    } = useViewedAccountAddress();
    if (!isDefined(row.original.asset)) {
      return null;
    }
    const identifier = InterfaceChainUtils.getIdentifierById(row.original.chainId);
    return <InternalLink href={getVaultUrl({
      identifier,
      vaultAddress: row.original.vaultAddress,
      vaultName: row.original.vaultName,
      hash: isSelf ? 'yourPosition' : undefined
    })} withHoverEffect>
          <VaultCell nameClassName="max-w-[240px]" address={row.original.vaultAddress} logoURI={row.original.asset.logoURI} name={row.original.vaultName} warnings={row.original.vaultWarnings} chainId={row.original.chainId} logoSize="small" isVaultV2={row.original.isVaultV2} className="gap-2xs" />
        </InternalLink>;
  },
  enableSorting: false,
  skeletonCell: <Skeleton className="h-md w-[172px] rounded-[20px]" />
}, {
  id: EARN_COLUMN_IDS.Type,
  accessorFn: row => row.type,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.Type}</span>
        </TooltipTrigger>
        <TooltipContent>
          The nature of the transaction, providing more details about the type of transaction.
        </TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <CellSimpleText>{row.original.typeText}</CellSimpleText>,
  enableSorting: false,
  skeletonCell: <Skeleton className="h-md w-[90px] rounded-[20px]" />,
  filterFn: 'arrIncludesSome'
}, {
  id: EARN_COLUMN_IDS.AssetsUsd,
  accessorFn: row => row.amountUsd,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.AssetsUsd}</span>
        </TooltipTrigger>
        <TooltipContent>The amount of tokens involved in the transaction.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    if (!isDefined(row.original.asset)) {
      return null;
    }
    const value = isDefined(row.original.amountUsd) ? safeParseNumber(row.original.amountUsd) : undefined;
    return <CellTokenAmountPrice token={row.original.asset} amount={row.original.amount} value={value} withAvatar />;
  },
  enableSorting: false,
  skeletonCell: <Skeleton className="h-md w-[150px] rounded-[20px]" />
}, {
  id: EARN_COLUMN_IDS.TransactionHash,
  accessorFn: row => row.transactionHash,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{
        marginLeft: '12px'
      }}>
            {COLUMN_LABELS.TransactionHash}
          </span>
        </TooltipTrigger>
        <TooltipContent>The transaction hash.</TooltipContent>
      </Tooltip>,
  cell: function TransactionHashCell({
    row
  }) {
    const buttonProps = useMemo(() => ({
      variant: 'ghost' as const,
      size: 'default' as const,
      className: 'hover:bg-accent active:bg-accent hover:brightness-100',
      label: abbreviateAddress(row.original.transactionHash)
    }), [row.original.transactionHash]);
    return <ButtonLink href={row.original.urlHash} mode="external" buttonProps={buttonProps} />;
  },
  enableSorting: false,
  skeletonCell: <Skeleton className="h-md w-[122px] rounded-[20px]" />,
  filterFn: 'includesString'
}];